/**
 * **The gym sticker**: the gym's avatar and its name on a white pill, laid
 * over the media in the share editor and moved and sized like the card.
 *
 * Everything is measured in the pill's height, so it is the same shape at
 * every size; its width follows from the name. `gymStickerAspect` gives that
 * shape (height over width) for the placement rules, which keep a sticker's
 * width only (see `thumbnail-placement.ts`).
 */

export interface GymStickerInfo {
  name: string;
  avatar: HTMLImageElement | null;
}

/** Shares of the pill's height. */
const PAD_LEFT = 0.12;
const AVATAR = 0.76;
const GAP = 0.2;
const FONT = 0.36;
const PAD_RIGHT = 0.4;
/** Longer names are cut short with an ellipsis. */
const MAX_NAME = 32;
const FONT_FAMILY = 'system-ui, -apple-system, "Segoe UI", sans-serif';

function shortName(name: string): string {
  return name.length > MAX_NAME ? `${name.slice(0, MAX_NAME - 1)}…` : name;
}

const widths = new Map<string, number>();

/** How wide the name is written on a pill one pixel high (measured once per name: a video asks every frame). */
function nameWidth(name: string): number {
  const known = widths.get(name);
  if (known != null) return known;
  const width = measureName(name);
  widths.set(name, width);
  return width;
}

function measureName(name: string): number {
  const ctx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
  if (!ctx) return shortName(name).length * FONT * 0.56;
  ctx.font = `600 ${FONT * 100}px ${FONT_FAMILY}`;
  return ctx.measureText(shortName(name)).width / 100;
}

/** The pill's height over its width, for this name. */
export function gymStickerAspect(name: string): number {
  return 1 / (PAD_LEFT + AVATAR + GAP + nameWidth(name) + PAD_RIGHT);
}

/** The pill drawn at (x, y), `width` wide. Without the avatar (none, or not loaded) a grey disc with the initial. */
export function drawGymSticker(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, info: GymStickerInfo) {
  const h = width * gymStickerAspect(info.name);
  const r = h / 2;

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = h * 0.15;
  ctx.beginPath();
  ctx.arc(x + r, y + r, r, Math.PI / 2, Math.PI * 1.5);
  ctx.arc(x + width - r, y + r, r, -Math.PI / 2, Math.PI / 2);
  ctx.closePath();
  ctx.fillStyle = 'rgba(255,255,255,0.96)';
  ctx.fill();
  ctx.restore();

  const d = AVATAR * h;
  const cx = x + PAD_LEFT * h + d / 2;
  const cy = y + r;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, d / 2, 0, Math.PI * 2);
  ctx.clip();
  const avatar = info.avatar;
  if (avatar && avatar.naturalWidth > 0) {
    // Filling the disc, as `object-fit: cover`.
    const scale = d / Math.min(avatar.naturalWidth, avatar.naturalHeight);
    const w = avatar.naturalWidth * scale;
    const ah = avatar.naturalHeight * scale;
    ctx.drawImage(avatar, cx - w / 2, cy - ah / 2, w, ah);
  } else {
    ctx.fillStyle = '#d1d5db';
    ctx.fillRect(cx - d / 2, cy - d / 2, d, d);
    ctx.fillStyle = '#374151';
    ctx.font = `700 ${d * 0.5}px ${FONT_FAMILY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(info.name.charAt(0).toUpperCase(), cx, cy);
  }
  ctx.restore();

  ctx.save();
  ctx.fillStyle = '#111827';
  ctx.font = `600 ${FONT * h}px ${FONT_FAMILY}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(shortName(info.name), x + (PAD_LEFT + AVATAR + GAP) * h, cy);
  ctx.restore();
}
